import { Button, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { useLEAccordionItemContext } from '../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import Loader from '../../../../components/Loader';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import {
  OtherLienholder,
  OtherLienholderSlug,
  isTreatedAsOther,
} from '../../../../constants/lienholders';
import { useTemporaryInfoUpdateMutation } from '../../../../gql/generated/graphql';
import { Lienholder } from '../../../../gql/prs/types';
import { CookieKeys, useCookie } from '../../../../hooks/useCookie';
import useImageLoader from '../../../../hooks/useImageLoader';
import useLienholders from '../../../../hooks/useLienholders';
import { RudderEvent, rudderanalytics } from '../../../../services/analytics/rudderstack';
import LienholderModal from '../../../GettingStarted/components/WhoIsYourLease/LienholderModal';
import LienholderSelector from '../../../GettingStarted/components/WhoIsYourLease/LienholderSelector';
import { lienholderNotAvailableInState } from '../../../GettingStarted/components/WhoIsYourLease/utils';

type LienholderInfo = {
  lienholder: string;
  other_lienholder: string;
};

const initialValues: LienholderInfo = {
  lienholder: '',
  other_lienholder: '',
};

const AffiliateSinglePageLienholder = ({
  showNextAccordion,
  setLienholderNotAvailable,
  licensePlateState,
  make,
}: {
  showNextAccordion: () => void;
  setLienholderNotAvailable: (value: boolean) => void;
  licensePlateState: string | null;
  make: string | null;
}) => {
  const { updateState, index, setOpenAccordionIndex } = useLEAccordionItemContext();

  const {
    lienholders,
    probableLienholders,
    loading: lienholdersLoading,
  } = useLienholders({
    make,
  });

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const probableLienholdersDisplayNames = probableLienholders
    .map((pLh: Lienholder) => pLh.display_name)
    .sort();

  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();

  const onSubmit = async (values: LienholderInfo) => {
    const selectedLienholder = lienholders.find(
      (lh: Lienholder) => lh.display_name === values.lienholder,
    );

    const treatedAsOther = isTreatedAsOther({
      lienholder: values.lienholder,
      lienholder_slug: selectedLienholder?.slug,
      is_core: selectedLienholder?.is_core,
    });
    // Non-core selections are stored as Other (slug "other" + the name in
    // other_lienholder), so downstream sees exactly what a manual entry produces.
    const lienholderSlug = treatedAsOther ? OtherLienholderSlug : selectedLienholder?.slug ?? '';

    await updateTemporaryInfo({
      variables: {
        info: {
          id: guid,
          data: {
            lienholder: values.lienholder,
            lienholderLogo: selectedLienholder?.logo_url,
            lienholder_slug: lienholderSlug,
            // A non-core selection mirrors a manual "Other" entry: its name is
            // persisted as other_lienholder for downstream consumers (deal notes).
            other_lienholder:
              values.lienholder === OtherLienholder
                ? values.other_lienholder
                : treatedAsOther
                ? values.lienholder
                : '',
          },
        },
      },
    });

    setIsModalOpen(false);

    if (
      lienholderNotAvailableInState(licensePlateState || null, selectedLienholder?.slug) ||
      (!treatedAsOther && !selectedLienholder?.enabled)
    ) {
      setLienholderNotAvailable(true);
      return;
    }

    showNextAccordion();
    setTimeout(() => {
      updateState({ status: 'complete' });
      setOpenAccordionIndex(index + 1);
    }, 0);
  };

  const loadingImages = useImageLoader(probableLienholders.map((lh) => lh.logo_url));

  if (lienholdersLoading || loadingImages) {
    return <Loader />;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object({
        lienholder: Yup.string().required('Please select a lienholder'),
        other_lienholder: Yup.string().when('lienholder', {
          is: OtherLienholder,
          then: (schema) => schema.required('Please enter lienholder name'),
        }),
      })}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, errors, values }) => {
        return (
          <Form>
            <Flex direction="column" alignItems="center">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="22px">
                {probableLienholders.map((probableLh) => (
                  <LienholderSelector
                    key={probableLh?.display_name}
                    lienholder={probableLh?.display_name}
                    lienholderLogo={probableLh?.logo_url}
                  />
                ))}
                {values.lienholder &&
                  !probableLienholdersDisplayNames?.includes(values.lienholder) && (
                    <LienholderSelector
                      key={values.lienholder}
                      lienholder={values.lienholder}
                      lienholderLogo={
                        lienholders.find((lh: Lienholder) => lh.display_name === values.lienholder)
                          ?.logo_url
                      }
                      otherLienholder={values.other_lienholder}
                    />
                  )}
              </SimpleGrid>
              {errors.lienholder && (
                <Text color="red" fontSize="14px" mt={5}>
                  {errors.lienholder}
                </Text>
              )}
              <Button
                variant="link"
                onClick={() => {
                  rudderanalytics.track(RudderEvent.OtherLienholderModalOpen);
                  setIsModalOpen(true);
                }}
                mt="20px"
              >
                See more options
              </Button>
              <PrimaryButton w="fit-content" type="submit" loading={isSubmitting} mt="30px" />
            </Flex>
            <LienholderModal
              licensePlateState={licensePlateState}
              isOpen={isModalOpen}
              closeModal={() => setIsModalOpen(false)}
              lienholders={lienholders}
              handleSubmit={() => setIsModalOpen(false)}
              skipLienholderCheck
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default AffiliateSinglePageLienholder;
